import {
	Component,
	effect,
	inject,
	input,
	OnDestroy,
	OnInit,
	signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { IonNav } from '@ionic/angular/common';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import {
	ReservationCheckoutModeEnum,
	TenantSettingsDto,
	TenantSettingsIntegrationsDto,
	TenantSettingsESignaturesDto,
	TenantSettingsIntegrationsSaferpayDto,
	TenantSettingsIntegrationsTrackSolidDto,
	UpdateTenantSettingsDto,
} from '@splitsecondconnect/ultramarine-shared';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { SingleDropdownComponent } from '../../../shared/components/dropdowns/single-dropdown/single-dropdown.component';
import { InputFieldComponent } from '../../../shared/components/input-field/input-field.component';
import { ToggleNewComponent } from '../../../shared/components/toggle-new/toggle-new.component';
import { UltraButtonComponent } from '../../../shared/components/ultramarine-ui/ultra-button/ultra-button.component';
import { UltraPrimaryButtonComponent } from '../../../shared/components/ultramarine-ui/ultra-primary/ultra-primary.component';
import {
	isoCodeMapper,
	languageIdMapper,
} from '../../../shared/mapper/language-id.mapper';
import { SelectItem } from '../../../shared/models/select-item.type';
import { BreakPointService } from '../../../shared/services/breakpoint.service';
import { PreferencesService } from '../../../shared/services/preferences.service';
import { ColorTheme } from '../../../shared/services/theme.service';
import { SettingsModel } from '../settings/models/settings.model';
import { SettingsService } from '../settings/services/settings.service';

import { IntegrationSecretHints } from './interfaces/integration-secret-hints.interface';

@Component({
	selector: 'app-preferences',
	templateUrl: 'preferences.component.html',
	styleUrls: ['preferences.component.scss'],
	imports: [
		TranslocoModule,
		InputFieldComponent,
		ToggleNewComponent,
		ReactiveFormsModule,
		FormsModule,
		UltraPrimaryButtonComponent,
		IonicModule,
		UltraButtonComponent,
		SingleDropdownComponent,
	],
})
export class PreferencesComponent implements OnInit, OnDestroy {
	isDesktop = toSignal(inject(BreakPointService).isDesktop$);
	#preferences = inject(PreferencesService);
	#formBuilder = inject(FormBuilder);
	#settingsService = inject(SettingsService);
	#transloco = inject(TranslocoService);
	#langNames = new Intl.DisplayNames(['en'], { type: 'language' });

	langs: SelectItem<string>[] = this.#transloco
		.getAvailableLangs()
		.map((lang) => {
			const id: string =
				typeof lang === 'string' ? lang : String(lang.id ?? '');

			return {
				value: id,
				displayValue: this.#langNames.of(id) ?? id.toUpperCase(),
			};
		});

	#destroy$ = new Subject<void>();
	isDarkMode = signal(false);
	reservationModeOptions: SelectItem<ReservationCheckoutModeEnum>[] = [
		{
			value: ReservationCheckoutModeEnum.WITH_PAYMENT,
			displayValue:
				'preferences|integrations|payment-providers|reservation-mode|with-payment',
		},
		{
			value: ReservationCheckoutModeEnum.WITHOUT_PAYMENT,
			displayValue:
				'preferences|integrations|payment-providers|reservation-mode|without-payment',
		},
	];
	preferencesForm = this.#formBuilder.group({
		logoUri: [null],
		isTaxIncluded: [null as boolean | null],
		availabilityThreshold: [null],
		reservationMode: [null as ReservationCheckoutModeEnum],
		language: [] as unknown as [string],
		integrations: this.#formBuilder.group({
			gpsTrackers: this.#formBuilder.group({
				isEnabled: [false],
				trackSolid: this.#formBuilder.group({
					baseUrl: [''],
					account: [''],
					appKey: [''],
					appSecret: [''],
					password: [''],
				}),
			}),
			paymentProviders: this.#formBuilder.group({
				isEnabled: [false],
				apiPaymentBaseUrl: [''],
				saferpay: this.#formBuilder.group({
					apiUsername: [''],
					apiPassword: [''],
					customerId: [''],
					terminalId: [''],
					securePayGateTerminalId: [''],
				}),
			}),
			eSignatures: this.#formBuilder.group({
				isEnabled: [false],
				senderSignerName: [''],
				senderSignerEmail: [''],
			}),
		}),
	});
	#settings = signal<TenantSettingsDto>(null);
	integrationSecretHints = signal<IntegrationSecretHints>({
		trackSolid: {
			isAccountConfigured: false,
			isAppKeyConfigured: false,
			isAppSecretConfigured: false,
			isPasswordConfigured: false,
		},
		saferpay: {
			isApiUsernameConfigured: false,
			isApiPasswordConfigured: false,
			isCustomerIdConfigured: false,
			isTerminalIdConfigured: false,
			isSecurePayGateTerminalIdConfigured: false,
		},
	});
	isSubmitting = signal(false);
	navReference = input<IonNav>();

	constructor() {
		effect(() => {
			this.#preferences.setTheme(this.isDarkMode());
		});
	}

	ngOnInit() {
		this.#preferences.theme$
			.pipe(takeUntil(this.#destroy$))
			.subscribe((theme) => {
				this.isDarkMode.set(theme === ColorTheme.DARK);
			});

		this.#applySettingsToForm(this.#settingsService.settings);
	}

	#applySettingsToForm(dto: TenantSettingsDto) {
		const formModel = this.#mapDtoToForm(dto);

		this.#settings.set(dto);
		this.#patchIntegrationHints(dto);
		this.preferencesForm.patchValue({
			logoUri: formModel.logoUri ?? null,
			isTaxIncluded: formModel.isTaxIncluded,
			availabilityThreshold: formModel.availabilityThreshold,
			reservationMode: dto.reservationMode ?? null,
			language: formModel.language,
			integrations: {
				gpsTrackers: {
					isEnabled: dto.integrations?.gpsTrackers?.isEnabled ?? false,
					trackSolid: {
						baseUrl: dto.integrations?.gpsTrackers?.trackSolid?.baseUrl ?? '',
						account: '',
						appKey: '',
						appSecret: '',
						password: '',
					},
				},
				paymentProviders: {
					isEnabled: dto.integrations?.paymentProviders?.isEnabled ?? false,
					apiPaymentBaseUrl:
						dto.integrations?.paymentProviders?.apiPaymentBaseUrl ?? '',
					saferpay: {
						apiUsername: '',
						apiPassword: '',
						customerId: '',
						terminalId: '',
						securePayGateTerminalId: '',
					},
				},
				eSignatures: {
					isEnabled: dto.integrations?.eSignatures?.isEnabled ?? false,
					senderSignerName:
						dto.integrations?.eSignatures?.senderSignerName ?? '',
					senderSignerEmail:
						dto.integrations?.eSignatures?.senderSignerEmail ?? '',
				},
			},
		});
	}

	#patchIntegrationHints(dto: TenantSettingsDto) {
		const ts = dto.integrations?.gpsTrackers?.trackSolid;
		const sp = dto.integrations?.paymentProviders?.saferpay;

		this.integrationSecretHints.set({
			trackSolid: {
				isAccountConfigured: Boolean(ts?.isAccountConfigured),
				isAppKeyConfigured: Boolean(ts?.isAppKeyConfigured),
				isAppSecretConfigured: Boolean(ts?.isAppSecretConfigured),
				isPasswordConfigured: Boolean(ts?.isPasswordConfigured),
			},
			saferpay: {
				isApiUsernameConfigured: Boolean(sp?.isApiUsernameConfigured),
				isApiPasswordConfigured: Boolean(sp?.isApiPasswordConfigured),
				isCustomerIdConfigured: Boolean(sp?.isCustomerIdConfigured),
				isTerminalIdConfigured: Boolean(sp?.isTerminalIdConfigured),
				isSecurePayGateTerminalIdConfigured: Boolean(
					sp?.isSecurePayGateTerminalIdConfigured,
				),
			},
		});
	}

	#mapDtoToForm(dto: TenantSettingsDto): SettingsModel {
		return {
			logoUri: dto.logoUri,
			isTaxIncluded: dto.isTaxIncluded,
			availabilityThreshold: dto.availabilityThreshold,
			language: languageIdMapper(
				dto.defaultLanguageId,
				this.#settingsService.languages,
			),
		};
	}

	maskIntegrationSecret(controlPath: string, isConfigured: boolean): boolean {
		if (!isConfigured) {
			return false;
		}

		const raw = this.preferencesForm.get(controlPath)?.value;
		const asString =
			raw === null || raw === undefined ? '' : String(raw).trim();

		return asString === '';
	}

	#nonEmpty(value: string | null | undefined): string | undefined {
		const trimmed = value?.trim();

		return trimmed ? trimmed : undefined;
	}

	#buildIntegrationsUpdate(): TenantSettingsIntegrationsDto | undefined {
		const raw = this.preferencesForm.getRawValue().integrations;
		const out: TenantSettingsIntegrationsDto = {};

		const gtForm = raw?.gpsTrackers;

		if (gtForm) {
			const tsForm = gtForm.trackSolid;
			const trackSolid: TenantSettingsIntegrationsTrackSolidDto = {};
			const baseUrl = this.#nonEmpty(tsForm?.baseUrl);

			if (baseUrl !== undefined) {
				trackSolid.baseUrl = baseUrl;
			}

			const account = this.#nonEmpty(tsForm?.account);

			if (account !== undefined) {
				trackSolid.account = account;
			}

			const appKey = this.#nonEmpty(tsForm?.appKey);

			if (appKey !== undefined) {
				trackSolid.appKey = appKey;
			}

			const appSecret = this.#nonEmpty(tsForm?.appSecret);

			if (appSecret !== undefined) {
				trackSolid.appSecret = appSecret;
			}

			const password = this.#nonEmpty(tsForm?.password);

			if (password !== undefined) {
				trackSolid.password = password;
			}

			out.gpsTrackers = {
				isEnabled: Boolean(gtForm.isEnabled),
				trackSolid,
			};
		}

		const ppForm = raw?.paymentProviders;

		if (ppForm) {
			const spForm = ppForm.saferpay;
			const saferpay: TenantSettingsIntegrationsSaferpayDto = {};
			const u = this.#nonEmpty(spForm?.apiUsername);

			if (u !== undefined) {
				saferpay.apiUsername = u;
			}

			const pw = this.#nonEmpty(spForm?.apiPassword);

			if (pw !== undefined) {
				saferpay.apiPassword = pw;
			}

			const customerId = this.#nonEmpty(spForm?.customerId);

			if (customerId !== undefined) {
				saferpay.customerId = customerId;
			}

			const terminalId = this.#nonEmpty(spForm?.terminalId);

			if (terminalId !== undefined) {
				saferpay.terminalId = terminalId;
			}

			const spg = this.#nonEmpty(spForm?.securePayGateTerminalId);

			if (spg !== undefined) {
				saferpay.securePayGateTerminalId = spg;
			}

			out.paymentProviders = {
				isEnabled: Boolean(ppForm.isEnabled),
				apiPaymentBaseUrl: ppForm.apiPaymentBaseUrl?.trim() ?? '',
				saferpay,
			};
		}

		const esForm = raw?.eSignatures;

		if (esForm) {
			const eSignatures: TenantSettingsESignaturesDto = {
				isEnabled: Boolean(esForm.isEnabled),
				senderSignerName: esForm.senderSignerName?.trim() ?? '',
				senderSignerEmail: esForm.senderSignerEmail?.trim() ?? '',
			};

			out.eSignatures = eSignatures;
		}

		if (!out.gpsTrackers && !out.paymentProviders && !out.eSignatures) {
			return undefined;
		}

		return out;
	}

	#mapFormToDto(): Partial<UpdateTenantSettingsDto> {
		const integrations = this.#buildIntegrationsUpdate();

		return {
			id: this.#settings().id,
			logoUri: this.preferencesForm.controls.logoUri.value,
			isTaxIncluded: this.preferencesForm.controls.isTaxIncluded.value,
			availabilityThreshold:
				this.preferencesForm.controls.availabilityThreshold.value,
			reservationMode:
				this.preferencesForm.controls.reservationMode.value ?? undefined,
			defaultLanguageId: isoCodeMapper(
				this.preferencesForm.controls.language.value,
				this.#settingsService.languages,
			),
			...(integrations ? { integrations } : {}),
		};
	}

	onSubmit() {
		this.isSubmitting.set(true);
		this.#settingsService
			.updateSettings(this.#mapFormToDto())
			.pipe(
				finalize(() => {
					this.isSubmitting.set(false);
				}),
			)
			.subscribe(() => {
				this.#applySettingsToForm(this.#settingsService.settings);
			});
	}

	onCancel() {
		this.#applySettingsToForm(this.#settingsService.settings);
		void this.navReference()?.popToRoot();
	}

	ngOnDestroy() {
		this.#destroy$.next();
		this.#destroy$.complete();
	}
}
